<template>
  <div>
    <paper-modal-container :is-visible="true" title="결제 방법 선택" @close="goBackToForm">
      <div v-if="loading" class="loading">로딩 중...</div>
      <div v-else-if="!draft" class="error-state">
        <p>결제 정보가 없습니다. 예매 폼으로 돌아가서 다시 시도해 주세요.</p>
        <button type="button" class="back-link-btn" @click="goBackToForm">예매 폼으로 돌아가기</button>
      </div>
      <div v-else class="payment-wrapper">
        <!-- 주문 요약 -->
        <section class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">주문 정보</h2>
            <p class="section-description">최종 결제 금액을 확인해 주세요</p>
          </div>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">공연명</span>
              <span class="info-value">{{ draft.liveTitle }}</span>
            </div>
            <div v-if="draft.reservationData?.name" class="info-row">
              <span class="info-label">예매자</span>
              <span class="info-value">{{ draft.reservationData.name }}</span>
            </div>
            <div v-if="draft.reservationData?.phone" class="info-row">
              <span class="info-label">연락처</span>
              <span class="info-value">{{ formatPhone(draft.reservationData.phone) }}</span>
            </div>

            <div v-if="draft.orderSummaryItems && draft.orderSummaryItems.length" class="order-summary">
              <div class="order-summary-title">구매 내역</div>
              <div v-for="(item, idx) in draft.orderSummaryItems" :key="'os-' + idx" class="order-summary-row">
                <div class="order-summary-name">{{ item.name }}</div>
                <div class="order-summary-detail">
                  {{ item.qty }}개 × {{ formatWon(item.unitPrice) }} =
                  <strong>{{ formatWon(item.subtotal) }}</strong>
                </div>
              </div>
              <div class="order-summary-total-row">
                <span class="order-summary-total-label">총 결제 금액</span>
                <span class="order-summary-total-value">{{ formatWon(draft.totalAmount) }}</span>
              </div>
            </div>
            <div v-else class="order-summary">
              <div class="order-summary-total-row">
                <span class="order-summary-total-label">총 결제 금액</span>
                <span class="order-summary-total-value">{{ formatWon(draft.totalAmount) }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 판매자 정보 -->
        <section v-if="hasOrganizerInfo" class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">판매자 정보</h2>
          </div>
          <div class="info-box">
            <div v-if="draft.organizerName" class="info-row">
              <span class="info-label">판매자명</span>
              <span class="info-value">{{ draft.organizerName }}</span>
            </div>
            <div v-if="draft.organizerContact" class="info-row">
              <span class="info-label">연락처</span>
              <span class="info-value">{{ draft.organizerContact }}</span>
            </div>
          </div>
        </section>

        <!-- 취소/환불 규정 -->
        <section class="form-section-block cancel-policy-section">
          <div class="section-header">
            <h2 class="section-title">취소/환불 규정</h2>
          </div>
          <div v-if="draft.cancelPolicy?.enabled && draft.cancelPolicy.content" class="cancel-policy-content"
            v-html="formatText(draft.cancelPolicy.content)">
          </div>
          <template v-else>
            <ul class="cancel-policy-default-list">
              <li>공연 시작일 3일 전까지: 결제 금액 전액 환불</li>
              <li>공연 시작일 2일 전: 결제 금액의 70% 환불</li>
              <li>공연 시작일 1일 전 및 당일 이후: 환불 불가</li>
            </ul>
            <p class="cancel-policy-default-note">링꾹라이브 이용약관 4조에 따른 기본 규정입니다.</p>
          </template>
        </section>

        <!-- 결제 수단 선택 -->
        <section class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">결제 수단</h2>
            <p class="section-description">원하시는 결제 수단을 선택해 주세요</p>
          </div>
          <div class="method-list">
            <button type="button" class="method-card" :class="{ selected: paymentMethod === 'bank' }"
              @click="paymentMethod = 'bank'" :disabled="processing">
              <div class="method-card-header">
                <span class="method-radio" :class="{ on: paymentMethod === 'bank' }">
                  <span v-if="paymentMethod === 'bank'" class="method-radio-dot"></span>
                </span>
                <span class="method-name">무통장 입금</span>
              </div>
              <p class="method-desc">계좌이체로 입금 후 예매 확정. 입금 확인까지 일부 시간이 소요될 수 있습니다.</p>
            </button>

            <button type="button" class="method-card" :class="{ selected: paymentMethod === 'card' }"
              @click="paymentMethod = 'card'" :disabled="processing || !isAdmin">
              <div class="method-card-header">
                <span class="method-radio" :class="{ on: paymentMethod === 'card' }">
                  <span v-if="paymentMethod === 'card'" class="method-radio-dot"></span>
                </span>
                <span class="method-name">일반결제 (카드)</span>
                <span v-if="!isAdmin" class="method-badge-warn">준비중</span>
                <span v-else-if="!tossClientKey" class="method-badge-warn">설정 필요</span>
              </div>
              <p v-if="!isAdmin" class="method-desc">일반결제(카드) 기능 준비중입니다.</p>
              <p v-else class="method-desc">토스페이먼츠 결제창으로 이동하여 카드로 즉시 결제합니다.</p>
            </button>
          </div>
        </section>

        <!-- 무통장 입금 안내 -->
        <section v-if="paymentMethod === 'bank'" class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">입금 계좌 안내</h2>
            <p class="section-description">아래 계좌로 입금해 주세요</p>
            <p v-if="depositNicknameText" class="deposit-nickname-notice">{{ depositNicknameText }}</p>
          </div>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">은행</span>
              <span class="info-value">{{ draft.deposit.bankName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">계좌번호</span>
              <span class="info-value info-value-account info-value-account-wrap">
                <span class="account-number-text">{{ draft.deposit.accountNumber }}</span>
                <button type="button" class="copy-btn-icon"
                  @click="copyAccountNumber(draft.deposit.bankName, draft.deposit.accountNumber)" title="계좌번호 복사"
                  aria-label="계좌번호 복사">
                  <img src="@/assets/images/file_copy_200.svg" alt="복사" />
                </button>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">예금주</span>
              <span class="info-value">{{ draft.deposit.accountHolder }}</span>
            </div>
          </div>
          <p class="deposit-warning-notice">※ 입금자명을 꼭 다시 한 번 확인해 주세요!</p>
        </section>

        <!-- 일반결제 안내 -->
        <section v-if="paymentMethod === 'card'" class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">카드 결제 안내</h2>
            <p class="section-description">결제 진행 시 외부 결제창이 열립니다</p>
          </div>
          <div class="info-box card-notice-box">
            <p class="card-notice-line">· 결제 완료 후 자동으로 예매가 확정됩니다.</p>
            <p class="card-notice-line">· 결제 중 창을 닫거나 취소하면 예매가 진행되지 않습니다.</p>
            <p v-if="!tossClientKey" class="card-notice-warn">
              ⚠ 토스페이먼츠 클라이언트 키가 설정되지 않았습니다 (VUE_APP_TOSS_CLIENT_KEY)
            </p>
          </div>
        </section>

        <!-- 액션 -->
        <div class="form-actions">
          <button type="button" class="secondary-btn" :disabled="processing" @click="goBackToForm">
            이전 단계로
          </button>
          <button type="button" class="submit-btn" :disabled="!canSubmit || processing" @click="handlePay">
            <template v-if="processing">처리 중...</template>
            <template v-else-if="paymentMethod === 'bank'">예매 신청 완료</template>
            <template v-else-if="paymentMethod === 'card'">{{ formatWon(draft.totalAmount) }} 결제하기</template>
            <template v-else>결제 수단을 선택해 주세요</template>
          </button>
        </div>

        <!-- 완료 메시지 -->
        <div v-if="bankSuccess" class="success-message">
          <p>✅ 예매 신청이 완료되었습니다!</p>
          <p>입금 확인 후 예매가 확정됩니다.</p>
        </div>
      </div>
    </paper-modal-container>
  </div>
</template>

<script>
import { loadTossPayments, ANONYMOUS } from "@tosspayments/tosspayments-sdk";
import { useUserStore } from "@/store/user";
import { createReservation } from "@/utils/write/ticket";
import { getTotalReservedQuantityForLive } from "@/utils/read/ticket";
import { trackReservationComplete } from "@/utils/activityTracker";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";

export default {
  name: "TicketPayment",
  components: { PaperModalContainer },
  data() {
    return {
      userStore: useUserStore(),
      liveId: this.$route.params.id,
      draft: null,
      loading: true,
      paymentMethod: "",
      processing: false,
      bankSuccess: false,
      tossClientKey: process.env.VUE_APP_TOSS_CLIENT_KEY || "",
    };
  },
  computed: {
    isAdmin() {
      return this.userStore.auth === "admin";
    },
    canSubmit() {
      if (!this.draft) return false;
      if (!this.paymentMethod) return false;
      if (this.paymentMethod === "card") {
        if (!this.isAdmin) return false;
        if (!this.tossClientKey) return false;
      }
      return true;
    },
    depositNicknameText() {
      const nickname = this.draft?.nickname || this.userStore.nickname || "";
      if (nickname) {
        return `"${nickname}"으로 입금해 주셔야 입금 확인됩니다`;
      }
      return "";
    },
    hasOrganizerInfo() {
      return !!(this.draft?.organizerName || this.draft?.organizerContact);
    },
  },
  mounted() {
    this.loadDraft();
  },
  methods: {
    loadDraft() {
      this.loading = true;
      try {
        const key = `ticketPaymentDraft:${this.liveId}`;
        const raw = sessionStorage.getItem(key);
        if (!raw) {
          this.draft = null;
          return;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.liveId !== this.liveId) {
          this.draft = null;
          return;
        }
        this.draft = parsed;
      } catch (e) {
        console.error("결제 데이터 로드 오류:", e);
        this.draft = null;
      } finally {
        this.loading = false;
      }
    },
    goBackToForm() {
      this.$router.push(`/ticket/${this.liveId}`);
    },
    formatWon(n) {
      const v = Number(n) || 0;
      return `${new Intl.NumberFormat("ko-KR").format(v)}원`;
    },
    formatPhone(phone) {
      if (!phone) return "";
      const digits = String(phone).replace(/[^0-9]/g, "");
      if (digits.length === 11) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return digits;
    },
    formatText(text) {
      if (!text) return "";
      return String(text).replace(/\n/g, "<br>");
    },
    async copyAccountNumber(bankName, accountNumber) {
      const bank = String(bankName || "").trim();
      const acct = String(accountNumber || "").trim();
      const text = [bank, acct].filter(Boolean).join(" ");
      if (!text) {
        this.$alert("복사할 계좌 정보가 없습니다.");
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        this.$alert("계좌 정보가 복사되었습니다.");
      } catch (e) {
        console.error("복사 오류:", e);
        this.$alert("복사에 실패했습니다.");
      }
    },
    async handlePay() {
      if (!this.canSubmit || this.processing) return;
      if (this.paymentMethod === "bank") {
        await this.handleBankPayment();
      } else if (this.paymentMethod === "card") {
        await this.handleCardPayment();
      }
    },
    // ============ 무통장 입금 ============
    async handleBankPayment() {
      this.processing = true;
      try {
        const liveData = this.draft?.reservationData;
        if (!liveData) {
          this.$alert("예매 정보가 없습니다.");
          return;
        }

        // 매수 가용성 재확인
        try {
          const totalReservedTickets = await getTotalReservedQuantityForLive(this.liveId);
          const requested = Number(liveData.quantity || 0);
          // linkookTicketCount는 폼 단계에서 이미 검증되었으므로 여기서는 안전망
          if (totalReservedTickets > 0 && requested > 0) {
            // 사전 체크 결과만 활용; 정확한 한도는 폼에서 처리됨
          }
        } catch (e) {
          console.error("매수 재확인 오류:", e);
        }

        const nickname = this.userStore.nickname || this.draft?.nickname || "닉네임";
        const message =
          `입금완료하셨나요?\n<span style="color:#66bdff;font-weight:600;">"${nickname}"으로 입금해주셔야 입금 확인됩니다.</span>\n<span style="color:#e03131;font-weight:600;font-size:12px;">※ 입금자명을 꼭 다시 한 번 확인해 주세요!</span>`;

        const totalPrice = Number(this.draft?.totalAmount || 0);
        const amountText = totalPrice > 0 ? this.formatWon(totalPrice) : "";
        const dep = this.draft?.deposit || {};

        const ok = await this.$confirm(message, {
          type: "ticketPaymentConfirm",
          bankName: dep.bankName || "국민은행",
          accountNumber: dep.accountNumber || "009901-04-203973",
          accountHolder: dep.accountHolder || "이민경 (링꾹)",
          amountText,
          amountSubText: "",
        });
        if (!ok) return;

        const reservationData = {
          ...liveData,
          paymentMethod: "bank",
          paymentConfirmed: false,
          status: "pending",
        };

        await createReservation(reservationData);
        trackReservationComplete(this.liveId);

        sessionStorage.removeItem(`ticketPaymentDraft:${this.liveId}`);
        this.bankSuccess = true;
        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          this.$router.push(`/live/${this.liveId}`);
        }, 2000);
      } catch (err) {
        console.error("무통장 예매 신청 오류:", err);
        this.$alert("예매 신청 중 오류가 발생했습니다. 다시 시도해 주세요.");
      } finally {
        if (!this.bankSuccess) {
          this.processing = false;
        }
      }
    },
    // ============ 토스페이먼츠 일반결제 ============
    async handleCardPayment() {
      if (!this.isAdmin) {
        this.$alert("일반결제(카드)는 관리자만 이용할 수 있습니다.");
        return;
      }
      if (!this.tossClientKey) {
        this.$alert("결제 모듈 설정이 필요합니다. 관리자에게 문의해 주세요.");
        return;
      }
      this.processing = true;
      try {
        const amount = Number(this.draft.totalAmount || 0);
        if (amount <= 0) {
          this.$alert("결제 금액이 올바르지 않습니다.");
          return;
        }

        const orderId = this.generateOrderId();
        const orderName = this.buildOrderName();

        // 결제 콜백에서 reservation 생성하기 위한 pending 데이터 저장
        try {
          sessionStorage.setItem(
            `ticketPaymentPending:${orderId}`,
            JSON.stringify({
              liveId: this.liveId,
              orderId,
              amount,
              reservationData: this.draft.reservationData,
              createdAt: Date.now(),
            })
          );
        } catch (e) {
          console.error("결제 진행 데이터 저장 오류:", e);
        }

        const tossPayments = await loadTossPayments(this.tossClientKey);
        const customerKey = this.userStore.uid || this.userStore.email || ANONYMOUS;
        const payment = tossPayments.payment({ customerKey });

        const successUrl = `${window.location.origin}/ticket/payment/success`;
        const failUrl = `${window.location.origin}/ticket/payment/fail`;

        await payment.requestPayment({
          method: "CARD",
          amount: { currency: "KRW", value: amount },
          orderId,
          orderName,
          successUrl,
          failUrl,
          customerEmail: this.userStore.email || undefined,
          customerName: this.draft.reservationData?.name || this.userStore.nickname || undefined,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
        // requestPayment 호출 후 사용자가 successUrl/failUrl로 리다이렉트되므로 이후 코드 실행되지 않음
      } catch (err) {
        console.error("결제창 호출 오류:", err);
        const message = err?.message || "결제창 호출 중 오류가 발생했습니다.";
        if (err?.code !== "USER_CANCEL") {
          this.$alert(message);
        }
        this.processing = false;
      }
    },
    generateOrderId() {
      const ts = Date.now().toString(36);
      const rand = Math.random().toString(36).slice(2, 10);
      return `lk-${this.liveId}-${ts}-${rand}`.slice(0, 60);
    },
    buildOrderName() {
      const items = this.draft?.orderSummaryItems || [];
      if (!items.length) {
        return this.draft?.liveTitle || "공연 예매";
      }
      const first = items[0]?.name || "티켓";
      if (items.length === 1) return first;
      return `${first} 외 ${items.length - 1}건`;
    },
  },
};
</script>

<style scoped>
.loading,
.error-state {
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #4a5568;
}

.error-state p {
  margin-bottom: 16px;
}

.back-link-btn {
  padding: 10px 20px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.back-link-btn:hover {
  background: #4da3ff;
}

.payment-wrapper {
  padding: 24px 0;
  text-align: left;
  max-width: 900px;
  margin: 0 auto;
}

.form-section-block {
  padding: 28px 0;
  border-bottom: 1px solid #e9ecef;
}

.form-section-block:last-of-type {
  border-bottom: none;
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deposit-nickname-notice {
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
}

.info-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.info-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  flex: 0 0 100px;
  font-weight: 600;
  color: #495057;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  margin: auto;
}

.info-value {
  flex: 1;
  color: #212529;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
}

.info-value-account {
  font-family: "NanumSquareRoundB", sans-serif;
  letter-spacing: 0.5px;
  color: #1e40af;
}

.info-value-account-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.account-number-text {
  word-break: break-all;
}

.copy-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 6px;
}

.copy-btn-icon:hover {
  background: #e9ecef;
}

.copy-btn-icon img {
  width: 16px;
  height: 16px;
  opacity: 0.6;
}

.deposit-warning-notice {
  margin: 12px 0 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #e03131;
  font-family: "NanumSquareRoundB", sans-serif;
}

.order-summary {
  padding-top: 16px;
}

.order-summary-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  color: #868e96;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.order-summary-row {
  padding: 8px 0;
}

.order-summary-row:last-of-type {
  border-bottom: none;
}

.order-summary-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
  margin-bottom: 2px;
}

.order-summary-detail {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #868e96;
}

.order-summary-detail strong {
  color: #495057;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.order-summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 4px;
  margin-top: 4px;
  border-top: 1px dashed #dee2e6;
}

.order-summary-total-label {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  color: #212529;
}

.order-summary-total-value {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #1976d2;
}

.cancel-policy-content {
  font-size: 14px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.8;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 4px solid #66bdff;
  white-space: pre-wrap;
}

.cancel-policy-default-list {
  margin: 0 0 10px 0;
  padding-left: 20px;
  list-style: disc;
}

.cancel-policy-default-list li {
  font-size: 14px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.9;
}

.cancel-policy-default-note {
  margin: 0;
  font-size: 12px;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
}

.method-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.method-card {
  display: block;
  width: 100%;
  text-align: left;
  padding: 18px 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
}

.method-card:hover:not(:disabled) {
  border-color: #cbd5e0;
}

.method-card.selected {
  border-color: #66bdff;
  background: rgba(102, 189, 255, 0.06);
  box-shadow: 0 2px 12px rgba(102, 189, 255, 0.12);
}

.method-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.method-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.method-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #cbd5e0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
}

.method-radio.on {
  border-color: #66bdff;
}

.method-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #66bdff;
}

.method-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 15px;
  color: #2d3748;
}

.method-badge-warn {
  margin-left: auto;
  font-size: 11px;
  color: #b26a00;
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 4px;
  padding: 2px 8px;
}

.method-desc {
  margin: 8px 0 0 28px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.card-notice-box {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
}

.card-notice-line {
  margin: 0 0 4px 0;
}

.card-notice-line:last-child {
  margin-bottom: 0;
}

.card-notice-warn {
  margin: 12px 0 0 0;
  padding: 10px 12px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 8px;
  color: #b26a00;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 30px;
}

.secondary-btn {
  flex: 0 0 auto;
  padding: 14px 22px;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.secondary-btn:hover:not(:disabled) {
  background: #e9ecef;
}

.secondary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  flex: 1;
  padding: 14px 24px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #4da3ff;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.success-message {
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(72, 187, 120, 0.4);
  z-index: 1000;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  min-width: 250px;
}

.success-message p {
  margin: 6px 0;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
}

@media (max-width: 480px) {
  .payment-wrapper {
    padding: 20px 12px;
  }

  .section-title {
    font-size: 17px;
  }

  .form-actions {
    flex-direction: column;
  }

  .secondary-btn,
  .submit-btn {
    width: 100%;
  }

  .info-label {
    flex: 0 0 80px;
    font-size: 12px;
  }

  .info-value {
    font-size: 13px;
  }
}
</style>
